<template>
  <div class="slider-container">
    <div class="slider-track">
      <div class="slider-labels">
        <span class="label-left">{{ question.leftLabel }}</span>
        <span class="label-right">{{ question.rightLabel }}</span>
      </div>
      <div
        v-if="!allHeightsZero"
        class="slider-gradient"
        :style="{ clipPath: dynamicClipPath, background: gradientBackground }"
      ></div>
      <div class="emojis">
        <span
          v-for="(item, idx) in displayEmojis"
          :key="idx"
          class="emoji"
          role="button"
          tabindex="0"
          @click="selectIndex(idx)"
          @keydown.enter.prevent="selectIndex(idx)"
          @keydown.space.prevent="selectIndex(idx)"
        >
          {{ item.emoji }}
        </span>
      </div>
      <div class="numbers" v-if="scaleMetadata">
        <span
          v-for="(item, idx) in scaleMetadata"
          :key="idx"
          class="number"
          role="button"
          tabindex="0"
          @click="selectIndex(idx)"
          @keydown.enter.prevent="selectIndex(idx)"
          @keydown.space.prevent="selectIndex(idx)"
        >
          {{ item.text || item.id }}
        </span>
      </div>
    </div>
    <div class="slider-rail-container">
      <div class="slider-rail"></div>
      <input
        type="range"
        :min="0"
        :max="availableValues.length - 1"
        :step="1"
        v-model="selectedIndex"
        class="slider"
        :style="{ '--thumb-color': currentThumbColor }"
      />
    </div>
  </div>
</template>
<script lang="ts">
import { Question, Serializer, ElementFactory, SvgRegistry } from 'survey-core'
import { getLocaleStrings } from 'survey-creator-core'
const value = ref(5)
const CUSTOM_TYPE = 'scale-slider'

export class QuestionScaleSliderModel extends Question {
  constructor(name: string) {
    super(name)
    const gradientColors = [
      '#16EE94',
      '#3EEA8A',
      '#3EEA8A',
      '#5BE57F',
      '#F7B42C',
      '#F7B42C',
      '#F7B42C',
      '#FA7F4C',
      '#FA7F4C',
      '#FC6360',
      '#FD5C63',
    ]
    const emojis = ['😁', '', '', '', '', '😐', '', '', '', '', '😭']
    this.scaleMetadata = Array.from({ length: 11 }, (_, i) => ({
      value: i,
      text: String(i),
      color: gradientColors[i],
      emoji: emojis[i],
      height: i + 1,
    }))
    this.leftLabel = 'Très satisfait'
    this.rightLabel = 'Très insatisfait'
  }

  dispose() {
    super.dispose()
  }
  getType() {
    return CUSTOM_TYPE
  }
  get scaleMetadata() {
    return this.getPropertyValue('scaleMetadata')
  }

  set scaleMetadata(val) {
    const oldValue = this.getPropertyValue('scaleMetadata')
    this.setPropertyValue('scaleMetadata', val)

    this.propertyValueChanged('scaleMetadata', oldValue, val)

    this.lastMetadataSnapshot = JSON.stringify(val)
  }

  get autoSave() {
    return this.getPropertyValue('autoSave') !== false
  }

  set autoSave(val) {
    const oldValue = this.getPropertyValue('autoSave')
    this.setPropertyValue('autoSave', val)

    this.propertyValueChanged('autoSave', oldValue, val)

    // When the save toggle is activated, trigger a save
    if (val && this.survey && (this.survey as any).saveSurveyFunc) {
      setTimeout(() => {
        ;(this.survey as any).saveSurveyFunc()
      }, 50)

      // Reset the toggle after saving
      setTimeout(() => {
        this.setPropertyValue('autoSave', false)
        this.propertyValueChanged('autoSave', val, false)
      }, 100)
    }
  }

  get leftLabel() {
    return this.getPropertyValue('leftLabel')
  }

  set leftLabel(val) {
    const oldValue = this.getPropertyValue('leftLabel')
    this.setPropertyValue('leftLabel', val)

    this.propertyValueChanged('leftLabel', oldValue, val)
  }

  get rightLabel() {
    return this.getPropertyValue('rightLabel')
  }

  set rightLabel(val) {
    const oldValue = this.getPropertyValue('rightLabel')
    this.setPropertyValue('rightLabel', val)

    this.propertyValueChanged('rightLabel', oldValue, val)
  }

  get reverseScale() {
    return this.getPropertyValue('reverseScale') || false
  }

  set reverseScale(val) {
    const oldValue = this.getPropertyValue('reverseScale')
    this.setPropertyValue('reverseScale', val)

    this.propertyValueChanged('reverseScale', oldValue, val)

    if (this.scaleMetadata && this.scaleMetadata.length > 0) {
      this.toggleScaleValues()
    }
  }

  get reverseGradient() {
    return this.getPropertyValue('reverseGradient') || false
  }

  set reverseGradient(val) {
    const oldValue = this.getPropertyValue('reverseGradient')
    this.setPropertyValue('reverseGradient', val)

    this.propertyValueChanged('reverseGradient', oldValue, val)
  }

  get reverseHeights() {
    return this.getPropertyValue('reverseHeights') || false
  }

  set reverseHeights(val) {
    const oldValue = this.getPropertyValue('reverseHeights')
    this.setPropertyValue('reverseHeights', val)

    this.propertyValueChanged('reverseHeights', oldValue, val)
  }

  get reverseEmojis() {
    return this.getPropertyValue('reverseEmojis') || false
  }

  set reverseEmojis(val) {
    const oldValue = this.getPropertyValue('reverseEmojis')
    this.setPropertyValue('reverseEmojis', val)

    this.propertyValueChanged('reverseEmojis', oldValue, val)
  }

  private toggleScaleValues() {
    const metadata = [...this.scaleMetadata]
    const values = metadata.map((item) => item.value)
    const reversedValues = [...values].reverse()

    metadata.forEach((item, index) => {
      item.value = reversedValues[index]
      item.text = String(reversedValues[index])
    })

    this.scaleMetadata = metadata
  }
  toJSON(): any {
    const json = super.toJSON()
    json.scaleMetadata = this.scaleMetadata
    json.leftLabel = this.leftLabel
    json.rightLabel = this.rightLabel
    json.autoSave = this.autoSave
    json.reverseScale = this.reverseScale
    json.reverseGradient = this.reverseGradient
    json.reverseHeights = this.reverseHeights
    json.reverseEmojis = this.reverseEmojis
    return json
  }

  protected onPropertyValueChanged(name: string, oldValue: any, newValue: any) {
    super.onPropertyValueChanged(name, oldValue, newValue)

    if (name === 'scaleMetadata' && (!newValue || newValue.length === 0)) {
      const gradientColors = [
        '#16EE94',
        '#3EEA8A',
        '#3EEA8A',
        '#5BE57F',
        '#F7B42C',
        '#F7B42C',
        '#F7B42C',
        '#FA7F4C',
        '#FA7F4C',
        '#FC6360',
        '#FD5C63',
      ]
      const emojis = ['😁', '', '', '', '', '😐', '', '', '', '', '😭']
      this.scaleMetadata = Array.from({ length: 11 }, (_, i) => ({
        value: i,
        text: String(i),
        color: gradientColors[i],
        emoji: emojis[i],
        height: i + 1,
      }))
    }
  }
}

Serializer.addClass(
  CUSTOM_TYPE,
  [
    {
      name: 'scaleMetadata',
      type: 'itemvalues',
      category: 'general',
      visibleIndex: 1,
      default: [],
    },
    {
      name: 'leftLabel',
      type: 'text',
      category: 'general',
      visibleIndex: 3,
      default: 'Très satisfait',
      displayName: 'Left Label',
    },
    {
      name: 'rightLabel',
      type: 'text',
      category: 'general',
      visibleIndex: 4,
      default: 'Très insatisfait',
      displayName: 'Right Label',
    },
    {
      name: 'reverseScale',
      type: 'boolean',
      category: 'general',
      visibleIndex: 5,
      default: false,
      displayName: 'Reverse Scale Order',
    },
    {
      name: 'reverseGradient',
      type: 'boolean',
      category: 'general',
      visibleIndex: 6,
      default: false,
      displayName: 'Reverse Gradient Direction',
    },
    {
      name: 'reverseHeights',
      type: 'boolean',
      category: 'general',
      visibleIndex: 7,
      default: false,
      displayName: 'Reverse Bar Heights',
    },
    {
      name: 'reverseEmojis',
      type: 'boolean',
      category: 'general',
      visibleIndex: 8,
      default: false,
      displayName: 'Reverse Emoji Order',
    },
    {
      name: 'autoSave',
      type: 'boolean',
      category: 'general',
      visibleIndex: 9,
      default: false,
      displayName: 'Save Changes',
    },
  ],
  function () {
    return new QuestionScaleSliderModel('')
  },
  'question',
)

Serializer.addProperty('itemvalue', {
  name: 'height',
  type: 'number',
})

Serializer.addProperty('itemvalue', {
  name: 'text',
  type: 'number',
})

Serializer.addProperty('itemvalue', {
  name: 'color',
  type: 'color',
  showMode: 'form',
})

Serializer.addProperty('itemvalue', {
  name: 'emoji',
  type: 'text',
  showMode: 'form',
})

SvgRegistry.registerIcon(
  CUSTOM_TYPE,
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><title>tune-variant</title><path d="M8 13C6.14 13 4.59 14.28 4.14 16H2V18H4.14C4.59 19.72 6.14 21 8 21S11.41 19.72 11.86 18H22V16H11.86C11.41 14.28 9.86 13 8 13M8 19C6.9 19 6 18.1 6 17C6 15.9 6.9 15 8 15S10 15.9 10 17C10 18.1 9.1 19 8 19M19.86 6C19.41 4.28 17.86 3 16 3S12.59 4.28 12.14 6H2V8H12.14C12.59 9.72 14.14 11 16 11S19.41 9.72 19.86 8H22V6H19.86M16 9C14.9 9 14 8.1 14 7C14 5.9 14.9 5 16 5S18 5.9 18 7C18 8.1 17.1 9 16 9Z" /></svg>',
)

ElementFactory.Instance.registerElement(CUSTOM_TYPE, (name) => {
  return new QuestionScaleSliderModel(name)
})

const locale = getLocaleStrings('en')
locale.qt[CUSTOM_TYPE] = 'Scale Slider'

export default {
  inheritAttrs: false,
}
</script>

<script setup lang="ts">
const props = defineProps<{ question: QuestionScaleSliderModel }>()
const value = ref(5)

const scaleMetadata = ref(props.question.scaleMetadata || [])

const interpolate = (start: number, end: number, factor: number) => {
  return start + (end - start) * factor
}

const interpolateColor = (color1: string, color2: string, factor: number): string => {
  const hexToRgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
    return result
      ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16),
        }
      : { r: 0, g: 0, b: 0 }
  }

  const rgbToHex = (r: number, g: number, b: number) => {
    const toHex = (n: number) => {
      const hex = Math.round(n).toString(16)
      return hex.length === 1 ? '0' + hex : hex
    }
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`
  }

  const rgb1 = hexToRgb(color1)
  const rgb2 = hexToRgb(color2)

  const r = rgb1.r + (rgb2.r - rgb1.r) * factor
  const g = rgb1.g + (rgb2.g - rgb1.g) * factor
  const b = rgb1.b + (rgb2.b - rgb1.b) * factor

  return rgbToHex(r, g, b)
}

const findNextDefinedHeight = (startIndex: number): { value: number; index: number } | null => {
  for (let i = startIndex + 1; i < scaleMetadata.value.length; i++) {
    const item = scaleMetadata.value[i]
    if (item.height !== undefined && item.height !== null && item.height !== '') {
      return { value: Number(item.height), index: i }
    }
  }
  return null
}

const findPrevDefinedHeight = (startIndex: number): { value: number; index: number } | null => {
  for (let i = startIndex - 1; i >= 0; i--) {
    const item = scaleMetadata.value[i]
    if (item.height !== undefined && item.height !== null && item.height !== '') {
      return { value: Number(item.height), index: i }
    }
  }
  return null
}

const findNextDefinedColor = (startIndex: number): { value: string; index: number } | null => {
  for (let i = startIndex + 1; i < scaleMetadata.value.length; i++) {
    const item = scaleMetadata.value[i]
    if (item.color && item.color !== '') {
      return { value: item.color, index: i }
    }
  }
  return null
}

const findPrevDefinedColor = (startIndex: number): { value: string; index: number } | null => {
  for (let i = startIndex - 1; i >= 0; i--) {
    const item = scaleMetadata.value[i]
    if (item.color && item.color !== '') {
      return { value: item.color, index: i }
    }
  }
  return null
}

watch(
  () => props.question.scaleMetadata,
  (newValue, oldValue) => {
    scaleMetadata.value = newValue || []
  },
  { deep: true, immediate: true },
)

const numberPositions = computed(() => {
  if (!scaleMetadata.value || scaleMetadata.value.length === 0) {
    return Array.from({ length: 11 }, (_, i) => i * 10 + 1.5)
  }

  const totalItems = scaleMetadata.value.length
  return scaleMetadata.value.map((_: any, idx: number) => {
    return totalItems <= 1 ? 50 : (idx / (totalItems - 1)) * 100
  })
})

const dynamicClipPath = computed(() => {
  if (!scaleMetadata.value || scaleMetadata.value.length === 0) {
    return 'polygon(0 100%, 100% 0, 100% 100%, 0 100%)'
  }

  const heightValues = scaleMetadata.value
    .map((item: any) => {
      if (item.height !== undefined && item.height !== null && item.height !== '') {
        return Number(item.height)
      }
      return null
    })
    .filter((h: number | null) => h !== null)

  const hasCustomHeights = heightValues.length > 0
  const minHeight = hasCustomHeights ? Math.min(...heightValues) : 0
  const maxHeight = hasCustomHeights ? Math.max(...heightValues) : 100

  const points = scaleMetadata.value.map((item: any, idx: number) => {
    const x = numberPositions.value[idx]

    let heightPercentage
    if (item.height !== undefined && item.height !== null && item.height !== '') {
      let normalizedHeight = 0
      if (maxHeight !== minHeight) {
        normalizedHeight = ((Number(item.height) - minHeight) / (maxHeight - minHeight)) * 90
      } else {
        // All heights identical (possibly all zero) => treat as 0% to collapse
        normalizedHeight = 0
      }

      if (props.question.reverseHeights) {
        normalizedHeight = 90 - normalizedHeight
      }

      heightPercentage = Math.max(0, normalizedHeight)
    } else {
      const prevHeight = findPrevDefinedHeight(idx)
      const nextHeight = findNextDefinedHeight(idx)

      let interpolatedHeight: number

      if (prevHeight && nextHeight) {
        const factor = (idx - prevHeight.index) / (nextHeight.index - prevHeight.index)
        interpolatedHeight = interpolate(prevHeight.value, nextHeight.value, factor)
      } else if (prevHeight) {
        interpolatedHeight = prevHeight.value
      } else if (nextHeight) {
        interpolatedHeight = nextHeight.value
      } else {
        const scaleValue = item.value !== undefined && item.value !== null ? item.value : idx + 1
        const maxValue = Math.max(
          ...scaleMetadata.value.map((s: any) => (s.value !== undefined && s.value !== null ? s.value : 1)),
        )
        interpolatedHeight = ((maxValue - scaleValue + 1) / maxValue) * 70 + 20
      }

      let normalizedHeight = 0
      if (maxHeight !== minHeight) {
        normalizedHeight = ((interpolatedHeight - minHeight) / (maxHeight - minHeight)) * 90
      } else {
        normalizedHeight = 0
      }

      if (props.question.reverseHeights) {
        normalizedHeight = 90 - normalizedHeight
      }

      heightPercentage = Math.max(0, normalizedHeight)
    }

    const y = 100 - heightPercentage

    return `${x}% ${y}%`
  })

  const polygonPoints = ['0 100%', ...points, '100% 100%'].join(', ')

  return `polygon(${polygonPoints})`
})

// True when every bar height is explicitly 0 (string/number 0)
const allHeightsZero = computed(() => {
  if (!scaleMetadata.value || scaleMetadata.value.length === 0) return false
  return scaleMetadata.value.every((item: any) => Number(item.height) === 0)
})

const gradientBackground = computed(() => {
  if (!scaleMetadata.value || scaleMetadata.value.length === 0) {
    return 'linear-gradient(90deg, #16ee94 0%, #f7b42c 50%, #fd5c63 100%)'
  }

  const gradientStops = scaleMetadata.value
    .map((item: any, idx: number) => {
      const position = numberPositions.value[idx]

      let color: string
      if (item.color && item.color !== '') {
        color = item.color
      } else {
        const prevColor = findPrevDefinedColor(idx)
        const nextColor = findNextDefinedColor(idx)

        if (prevColor && nextColor) {
          const factor = (idx - prevColor.index) / (nextColor.index - prevColor.index)
          color = interpolateColor(prevColor.value, nextColor.value, factor)
        } else if (prevColor) {
          color = prevColor.value
        } else if (nextColor) {
          color = nextColor.value
        } else {
          color = '#ccc'
        }
      }

      return `${color} ${position}%`
    })
    .join(', ')

  const direction = props.question.reverseGradient ? '270deg' : '90deg'
  return `linear-gradient(${direction}, ${gradientStops})`
})

const availableValues = computed(() => {
  // Keep zero; fallback only when value is null/undefined
  return scaleMetadata.value.map((item: any, idx: number) =>
    item.value !== undefined && item.value !== null ? item.value : idx,
  )
})

const displayEmojis = computed(() => {
  if (!scaleMetadata.value || scaleMetadata.value.length === 0) {
    return []
  }

  if (props.question.reverseEmojis) {
    return scaleMetadata.value.map((item: any, idx: number) => {
      const reversedIndex = scaleMetadata.value.length - 1 - idx
      return {
        ...item,
        emoji: scaleMetadata.value[reversedIndex]?.emoji || '',
      }
    })
  }

  return scaleMetadata.value
})

const currentThumbColor = computed(() => {
  if (!scaleMetadata.value || scaleMetadata.value.length === 0) {
    return '#fd5c63'
  }

  const currentIndex = selectedIndex.value
  let targetIndex = currentIndex

  if (props.question.reverseGradient) {
    targetIndex = scaleMetadata.value.length - 1 - currentIndex
  }

  const currentItem = scaleMetadata.value[targetIndex]

  if (currentItem?.color && currentItem.color !== '') {
    return currentItem.color
  }

  const prevColor = findPrevDefinedColor(targetIndex)
  const nextColor = findNextDefinedColor(targetIndex)

  if (prevColor && nextColor) {
    const factor = (targetIndex - prevColor.index) / (nextColor.index - prevColor.index)
    return interpolateColor(prevColor.value, nextColor.value, factor)
  } else if (prevColor) {
    return prevColor.value
  } else if (nextColor) {
    return nextColor.value
  } else {
    return '#fd5c63'
  }
})

const selectedIndex = computed({
  get() {
    const currentValue = value.value
    const index = availableValues.value.findIndex((val: any) => val === currentValue)
    return index >= 0 ? index : 0
  },
  set(newIndex: number) {
    const newValue = availableValues.value[newIndex]
    if (newValue !== undefined) {
      value.value = newValue
      props.question.value = newValue
    }
  },
})

const selectIndex = (idx: number) => {
  selectedIndex.value = idx
}

watch(
  () => props.question.value,
  (newValue) => {
    if (newValue !== undefined && newValue !== value.value) {
      value.value = newValue
    }
  },
  { immediate: true },
)
</script>

<style lang="stylus" scoped>
.slider-container
  width 100%
  max-width 500px
  margin 24px auto
  font-family sans-serif

.slider-labels
  position absolute
  display flex
  justify-content space-between
  margin-bottom 8px
  font-weight bold
  bottom 65px
  width 100%
  z-index 2
  padding 0 20px
  gap 10px
  align-items flex-end

  .label-left,
  .label-right
    flex 1
    font-size 1rem
    white-space pre-wrap
    overflow hidden
    text-overflow ellipsis

    // Tablet view
    @media (max-width: 768px)
      font-size 0.8rem
    // Mobile view
    @media (max-width: 480px)
      font-size 0.6rem

    // Small mobile view
    @media (max-width: 360px)
      font-size 0.5rem
  .label-left
    text-align left
  .label-right
    text-align right

.slider-track
  position relative
  height 120px
  border-radius 12px
  overflow hidden

.slider-gradient
  position absolute
  top 0
  left 0
  width 100%
  height 100%
  background linear-gradient(90deg, #16ee94 0%, #f7b42c 50%, #fd5c63 100%)
  z-index 1

.emojis
  position absolute
  bottom 30px
  left 0
  width 100%
  height 32px
  pointer-events auto
  z-index 2
  display flex
  justify-content space-between
  align-items center
  padding 0 14px

.emoji
  font-size 2.2rem
  flex 1
  text-align center
  cursor pointer
  user-select none

.numbers
  position absolute
  bottom 0px
  left 0
  width 100%
  height 28px
  pointer-events auto
  z-index 2
  display flex
  justify-content space-between
  align-items center
  padding 0 14px

.number
  font-size 1.4rem
  color #222
  font-weight bold
  text-align center
  min-width 30px
  cursor pointer
  user-select none

.slider-rail
  position absolute
  left 24px
  right 24px
  bottom 15px
  height 8px
  border-radius 4px
  background #ccc
  z-index 2
  opacity 0.8
  box-shadow inset 0 1px 2px rgba(0, 0, 0, 0.15), 0 1px 1px rgba(255, 255, 255, 0.3)

.slider
  position absolute
  left 0
  bottom 0px
  width 100%
  margin 0
  -webkit-appearance none
  -moz-appearance none
  appearance none
  height 40px
  background transparent
  z-index 3
  padding 0 10px

.slider-rail-container
  position relative
  height 40px

.slider::-webkit-slider-thumb
  appearance none
  width 28px
  height 28px
  border-radius 50%
  background var(--thumb-color, #fd5c63)
  cursor pointer
  box-shadow 0 2px 6px rgba(0, 0, 0, 0.2), inset 0 1px 3px rgba(255, 255, 255, 0.8)
  margin-top -6px
  transition all 0.2s ease
  position relative

  &:hover
    transform scale(1.1)
    box-shadow 0 3px 8px rgba(0, 0, 0, 0.3), inset 0 1px 3px rgba(255, 255, 255, 0.8)

  &:active
    transform scale(0.95)

.slider::-moz-range-thumb
  width 28px
  height 28px
  border-radius 50%
  background var(--thumb-color, #fd5c63)
  cursor pointer
  transition all 0.2s ease
  box-shadow 0 2px 6px rgba(0, 0, 0, 0.2), inset 0 1px 3px rgba(255, 255, 255, 0.8)

  &:hover
    transform scale(1.1)

.slider::-ms-thumb
  width 28px
  height 28px
  border-radius 50%
  background linear-gradient(135deg, #fff 0%, #f8f9fa 100%)
  border 3px solid var(--thumb-color, #fd5c63)
  cursor pointer
  transition all 0.2s ease
  box-shadow 0 2px 6px rgba(0, 0, 0, 0.2), inset 0 1px 3px rgba(255, 255, 255, 0.8)

.slider::-webkit-slider-runnable-track
  height 16px
  border-radius 8px
  background transparent
  border none

.slider::-moz-range-track
  height 16px
  border-radius 8px
  background transparent
  border none

.slider::-ms-track
  height 16px
  border-radius 8px
  background transparent
  border none
  color transparent

.slider::-ms-fill-lower
  background transparent

.slider::-ms-fill-upper
  background transparent
</style>
